import React, { FC, Fragment, useState } from "react";
import cameraIcon from "shared/icons/camera.svg";
import closeButton from "shared/icons/close.svg";

const EditableItem: FC<{
  contentRef: any;
  contentWidth: number;
  pushNewItem?: () => void;
}> = ({ contentRef, contentWidth, pushNewItem }) => {
  const [iconHidden, setIconHidden] = useState(false);
  const [iconCameraHidden, setIconCameraHidden] = useState(false);
  const [displayHelper, setDisplayHelper] = useState(false);
  const [showInput, setShowInput] = useState(false);

  // view button panel
  const handleSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.toString() !== "") {
      setDisplayHelper(true);
    } else {
      setDisplayHelper(false);
    }
  };

  // click on camera icon
  const handleCameraIconClick = () => {
    setShowInput(true);
  };

  // add new img in container
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const img = document.createElement("img");
        img.src = reader.result as string;
        contentRef.current.appendChild(img);
        if (pushNewItem) {
          // проверка на существование pushNewItem
          pushNewItem();
        }
      };
      reader.readAsDataURL(file);
    }
    setShowInput(false);
  };

  // text editor
  const applyStyle = (style: string, value?: string) => {
    document.execCommand(style, false, value);
  };

  const changeFontSize = (size: string) => {
    document.execCommand("fontSize", false, size);
  };

  const toggleList = () => {
    document.execCommand("insertUnorderedList");
  };

  const wrapSelectionWithTag = (tag: string) => {
    const selection = window.getSelection();
    if (selection) {
      const range = selection.getRangeAt(0);
      const selectedText = range.toString();
      const newNode = document.createElement(tag);
      newNode.appendChild(document.createTextNode(selectedText));

      // Get the parent element of the selected text
      const parentElement = range.commonAncestorContainer.parentElement;

      // Replace the parent <li> element with the new <p> element
      if (parentElement && parentElement.tagName.toLowerCase() === "li") {
        parentElement.replaceWith(newNode);
      } else {
        // If the parent is not an <li> element, just insert the new <p> element
        range.deleteContents();
        range.insertNode(newNode);
      }
    }
  };

  const handleListButtonClick = (apply: boolean) => {
    if (apply) {
      wrapSelectionWithTag("li");
    } else {
      wrapSelectionWithTag("p");
    }
  };

  return (
    <Fragment>
      <div
        onMouseUp={handleSelection}
        onKeyDown={handleSelection}
        style={{
          display: "flex",
          alignItems: "flex-end",
          position: "relative",
          width: "100%",
        }}
      >
        {!iconHidden && (
          <>
            {showInput && (
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageUpload}
                ref={(input) => {
                  if (input) {
                    input.click();
                  }
                }}
              />
            )}
            <>
              {!iconCameraHidden && (
                <img
                  src={cameraIcon}
                  alt=""
                  style={{
                    marginRight: 8,
                    marginBottom: 12,
                    cursor: "pointer",
                  }}
                  onClick={handleCameraIconClick}
                />
              )}
            </>
          </>
        )}
        <div
          onDoubleClick={(event) => {
            event.stopPropagation();
          }}
          style={{
            wordWrap: "break-word",
            cursor: "text",
          }}
          placeholder={"Напишите что-нибудь"}
          className={`contentEditableItem w-full my-2 outline-none cursor-text overflow-y-scroll w-[${contentWidth}px] max-w-[${contentWidth}px]`}
          contentEditable
          ref={contentRef}
        />
        {displayHelper ? (
          <div
            style={{
              padding: 8,
              position: "absolute",
              zIndex: 999,
              top: -60,
              backgroundColor: "black",
              borderRadius: "8px",
              color: "white",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              maxWidth: "calc(100vw - 40px)",
              transform: "translateX(-50%)",
              left: "50%",
            }}
            className="transition-all duration-200"
          >
            <div className="flex flex-row items-center flex-wrap gap-1 justify-center w-full sm:flex-nowrap">
              <button
                className="text-xs sm:text-sm whitespace-nowrap hover:bg-gray-700 rounded transition-colors"
                style={{ cursor: "pointer", padding: "6px 8px" }}
                onClick={() => {
                  changeFontSize("6");
                }}
              >
                Заголовок
              </button>
              <button
                className="text-xs sm:text-sm whitespace-nowrap hover:bg-gray-700 rounded transition-colors"
                style={{ cursor: "pointer", padding: "6px 8px" }}
                onClick={() => {
                  changeFontSize("4.5");
                }}
              >
                Подзаголовок
              </button>
              <button
                className="text-xs sm:text-sm whitespace-nowrap hover:bg-gray-700 rounded transition-colors"
                style={{ cursor: "pointer", padding: "6px 8px" }}
                onClick={() => {
                  changeFontSize("3");
                  handleListButtonClick(false);
                }}
              >
                Обычный текст
              </button>
              <button
                className="text-xs sm:text-sm whitespace-nowrap hover:bg-gray-700 rounded transition-colors"
                style={{ cursor: "pointer", padding: "6px 8px" }}
                onClick={() => applyStyle("bold")}
              >
                Жирный
              </button>
              <button
                className="text-xs sm:text-sm whitespace-nowrap hover:bg-gray-700 rounded transition-colors"
                style={{ cursor: "pointer", padding: "6px 8px" }}
                onClick={() => applyStyle("italic")}
              >
                Курсив
              </button>
              <button
                className="text-xs sm:text-sm whitespace-nowrap hover:bg-gray-700 rounded transition-colors"
                style={{ cursor: "pointer", padding: "6px 8px" }}
                onClick={() => applyStyle("underline")}
              >
                Подчеркнутый
              </button>
              <button
                className="text-xs sm:text-sm whitespace-nowrap hover:bg-gray-700 rounded transition-colors"
                style={{ cursor: "pointer", padding: "6px 8px" }}
                onClick={() => handleListButtonClick(true)}
              >
                • Список
              </button>
              <button
                className="hover:bg-gray-700 rounded transition-colors ml-1 w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center"
                style={{
                  cursor: "pointer",
                  padding: "6px 6px",
                  borderRadius: "8px",
                }}
                onClick={() => setDisplayHelper(false)}
              >
                <img
                  src={closeButton}
                  alt="close"
                  className="w-4 h-4 sm:w-5 sm:h-5"
                />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </Fragment>
  );
};

export { EditableItem };
